import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, X } from 'lucide-react';
import { SearchResults } from './search/SearchResults';
import { API } from '../config/api';

export function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const performSearch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`, {
        withCredentials: true,
      });
      setResults(data.results || []);
      setShowResults(true);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (query.length >= 2) {
        performSearch();
      } else {
        setResults([]);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [query, performSearch]);

  const handleResultClick = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
  };

  return (
    <div className="relative w-full" ref={searchRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && setShowResults(true)}
          placeholder="Search clients, meetings, shoots..."
          className="w-full bg-surface-raised border border-border rounded-lg pl-10 pr-10 py-2 text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all"
          data-testid="global-search-input"
        />
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setResults([]);
              setShowResults(false);
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-muted hover:text-foreground"
            data-testid="clear-search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {showResults && query.length >= 2 && (
        <div className="absolute z-50 w-full mt-2 bg-surface border border-border rounded-lg shadow-lg max-h-96 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-text-secondary">
              <div className="text-sm">Searching...</div>
            </div>
          ) : (
            <SearchResults results={results} onSelect={handleResultClick} query={query} />
          )}
        </div>
      )}
    </div>
  );
}
